import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Table, Thead, Tbody, Tr, Th, Td, Divider
} from "@chakra-ui/react";
import { FaFlask, FaTint, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterBlogReverseOsmosis: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/reverse-osmosis-guide"
        fallbackTitle="Reverse Osmosis Australia: Complete Guide — One Water Australia"
        fallbackDescription="Is reverse osmosis the right water filtration method for your Australian home? Honest pros, cons, and when RO actually makes sense."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Complete Guide
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Reverse Osmosis Australia: Complete Guide
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              Honest pros, cons, and whether RO is right for your Australian home.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=1200&q=80"
          alt="Crystal clear purified water — reverse osmosis filtration"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* What is RO */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What Is Reverse Osmosis?</Heading>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Reverse osmosis is a water purification process that forces water through an extremely fine semi-permeable membrane — 0.0001 microns — under pressure. The membrane allows water molecules through while blocking dissolved solids, contaminants, and most impurities.
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              A typical residential RO system runs water through multiple stages:
            </Text>
            <HStack spacing={3} flexWrap="wrap" justify="center" mb={4}>
              {["Sediment pre-filter", "Carbon pre-filter", "RO membrane", "Carbon post-filter", "(Optional) UV or remineralisation"].map((stage, i, arr) => (
                <HStack key={stage} spacing={2}>
                  <Box px={3} py={2} bg={ICE_MIST} borderRadius="lg" fontSize="sm" color={NAVY} fontWeight="600">{stage}</Box>
                  {i < arr.length - 1 && <Text color={WATER_BLUE} fontSize="lg">→</Text>}
                </HStack>
              ))}
            </HStack>
            <Text color="gray.700" lineHeight="tall">
              Note: the carbon pre-filter is essential — chlorine degrades the RO membrane, so chlorinated mains water must be pre-filtered before reaching the membrane. This is one reason professional specification matters.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What RO removes */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What RO Removes (And What It Doesn't)</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <Text fontWeight="700" color={DEEP_AQUA} mb={4} fontSize="sm" textTransform="uppercase" letterSpacing="wider">Removed by RO</Text>
                <VStack align="flex-start" spacing={2}>
                  {["Dissolved solids (TDS)", "Heavy metals (lead, arsenic, chromium)", "Fluoride", "Nitrates and nitrites", "Sodium", "Pharmaceuticals and hormones (many)", "Most bacteria and viruses (membrane)", "Chloramines (with correct pre-filter)"].map((item) => (
                    <HStack key={item} spacing={2}>
                      <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} flexShrink={0} />
                      <Text fontSize="sm" color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Box>
                <Text fontWeight="700" color={DEEP_AQUA} mb={4} fontSize="sm" textTransform="uppercase" letterSpacing="wider">Not removed without pre-treatment</Text>
                <VStack align="flex-start" spacing={2}>
                  {["Chlorine (damages membrane — requires carbon pre-filter)", "Sediment (requires sediment pre-filter)", "Some VOCs (requires correct carbon media)", "Dissolved gases"].map((item) => (
                    <HStack key={item} spacing={2} align="flex-start">
                      <Text color={DEEP_AQUA} fontSize="sm" flexShrink={0} mt={0.5}>⚠</Text>
                      <Text fontSize="sm" color="gray.600">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Honest pros and cons */}
          <Box>
            <Heading size="lg" color={NAVY} mb={2}>The Honest Pros and Cons</Heading>
            <Text color="gray.600" mb={6} fontSize="sm">No filtration method is right for every situation. Here's an unvarnished look at reverse osmosis.</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box p={6} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
                <Text fontWeight="700" color={NAVY} mb={4}>Advantages</Text>
                <VStack align="flex-start" spacing={3}>
                  {[
                    { pro: "Widest contaminant range", detail: "No other residential technology removes as broad a spectrum of dissolved contaminants." },
                    { pro: "Very high purity output", detail: "Typical TDS reduction of 90–99%. Produces the purest drinking water available in a residential system." },
                    { pro: "Effective against dissolved solids", detail: "Uniquely effective for bore water, high-TDS water, or heavy metal concerns." },
                    { pro: "No chemicals required", detail: "Physical membrane process — no disinfection chemicals needed at the RO stage." },
                  ].map((item) => (
                    <Box key={item.pro}>
                      <Text fontWeight="600" fontSize="sm" color={NAVY}>{item.pro}</Text>
                      <Text fontSize="sm" color="gray.600">{item.detail}</Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
              <Box p={6} bg="gray.50" borderRadius="xl">
                <Text fontWeight="700" color={NAVY} mb={4}>Limitations</Text>
                <VStack align="flex-start" spacing={3}>
                  {[
                    { con: "Water waste", detail: "Produces 2–4L of wastewater for every 1L of purified output. A real consideration for water-conscious households." },
                    { con: "Mineral removal", detail: "Removes beneficial minerals (calcium, magnesium) as well as contaminants. Remineralisation stage can address this." },
                    { con: "Slower flow rate", detail: "RO systems produce water more slowly than unfiltered taps — usually supplemented by a storage tank." },
                    { con: "Requires pre-filtration", detail: "Chlorine and sediment will damage the membrane without proper pre-filtration — professional specification is important." },
                    { con: "Not always necessary", detail: "For mains-supplied homes primarily concerned with chlorine and taste, a carbon filter is likely sufficient." },
                  ].map((item) => (
                    <Box key={item.con}>
                      <Text fontWeight="600" fontSize="sm" color={NAVY}>{item.con}</Text>
                      <Text fontSize="sm" color="gray.600">{item.detail}</Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* When RO makes sense */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>When RO Makes Sense in Australia</Heading>
            <VStack spacing={3} align="stretch">
              {[
                { title: "Bore or tank water", desc: "High TDS, dissolved minerals, or biological risk. RO paired with UV is standard specification for these supplies." },
                { title: "Heavy metal or nitrate concerns", desc: "Near industrial sites, agricultural runoff areas, or older properties with lead solder plumbing." },
                { title: "Fluoride removal preference", desc: "Carbon filtration does not remove fluoride. RO does. For households who prefer fluoride-free drinking water." },
                { title: "Elevated TDS from any source", desc: "Hard water areas, coastal or near-desalination zones where mineral content is elevated." },
                { title: "Specific health-driven requirements", desc: "Where medical or personal health circumstances call for the highest possible water purity." },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={1} fontSize="sm">{item.title}</Text>
                  <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* RO vs other methods table */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>RO vs Other Methods</Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg={ICE_MIST}>
                  <Tr>
                    <Th color={NAVY} py={4}>Method</Th>
                    <Th color={NAVY} py={4}>Removes chlorine</Th>
                    <Th color={NAVY} py={4}>Removes dissolved solids</Th>
                    <Th color={NAVY} py={4}>Biological protection</Th>
                    <Th color={NAVY} py={4}>Scope</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    { method: "Carbon filter", chlorine: "✓ Yes", dissolved: "✕ No", bio: "✕ No", scope: "Point of use or whole-home" },
                    { method: "Reverse osmosis", chlorine: "✓ (pre-filter)", dissolved: "✓ Yes", bio: "Partial (membrane)", scope: "Point of use" },
                    { method: "UV", chlorine: "✕ No", dissolved: "✕ No", bio: "✓ Yes", scope: "Whole-home (with pre-filter)" },
                    { method: "Whole-home carbon", chlorine: "✓ Yes", dissolved: "✕ No", bio: "✕ No", scope: "Whole property" },
                    { method: "RO + UV system", chlorine: "✓ (pre-filter)", dissolved: "✓ Yes", bio: "✓ Yes", scope: "Point of use (high specification)" },
                  ].map((row) => (
                    <Tr key={row.method} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="600" color={NAVY} fontSize="sm">{row.method}</Td>
                      <Td fontSize="sm" color={row.chlorine.includes("✓") ? DEEP_AQUA : "gray.400"}>{row.chlorine}</Td>
                      <Td fontSize="sm" color={row.dissolved.includes("✓") ? DEEP_AQUA : "gray.400"}>{row.dissolved}</Td>
                      <Td fontSize="sm" color={row.bio.includes("✓") ? DEEP_AQUA : "gray.400"}>{row.bio}</Td>
                      <Td fontSize="sm" color="gray.600">{row.scope}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Best system depends on your water. We start with assessment, not product recommendation.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* How One Water approaches RO */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>How One Water Approaches RO</Heading>
            <Box p={6} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={6}>
              <Text color="gray.700" fontSize="lg" lineHeight="tall" fontStyle="italic">
                "Assess first, recommend second. If RO isn't right, we'll say so."
              </Text>
            </Box>
            <Text color="gray.700" lineHeight="tall">
              Reverse osmosis is one of the most powerful residential water treatment technologies available — and one of the most frequently over-specified. Our assessment process identifies whether RO is genuinely warranted for your water supply before recommending it.
            </Text>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Heading size="lg" color="white">Not sure if RO is right for you?</Heading>
              <Text color="whiteAlpha.800">
                A water assessment gives you the data. Book a free assessment — we'll tell you whether RO makes sense for your supply.
              </Text>
              <Button
                as={RouterLink}
                to="/contact"
                bg={WATER_BLUE}
                color={NAVY}
                size="lg"
                fontWeight="bold"
                _hover={{ bg: "#8DC5E0" }}
                transition="all 0.2s"
              >
                Book a Free Water Assessment
              </Button>
            </VStack>
          </Box>

          {/* Internal links */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Related Reading</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              {[
                { title: "RO vs Whole-Home Filtration", href: "/water/blog/reverse-osmosis-vs-whole-home" },
                { title: "Under-Sink Water Purifier", href: "/water/water-purifiers/under-sink-system" },
                { title: "Water Filtration Systems", href: "/water/water-filtration" },
                { title: "Is Tap Water Safe?", href: "/water/blog/tap-water-safe-australia" },
                { title: "Gold Coast Water Quality", href: "/water/water-quality/gold-coast" },
              ].map((l) => (
                <Box
                  key={l.title}
                  as={RouterLink}
                  to={l.href}
                  p={3}
                  bg="gray.50"
                  borderRadius="lg"
                  display="block"
                  textDecoration="none"
                  _hover={{ bg: ICE_MIST }}
                  transition="all 0.2s"
                >
                  <Text fontSize="sm" color={NAVY} fontWeight="500">{l.title}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogReverseOsmosis;
